import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Award,
  Bike,
  Calendar as CalendarIcon,
  Clock,
  Compass,
  Leaf,
  MapPin,
  Mountain,
  Search,
  Sailboat,
  Ship,
  Snowflake,
  User,
  Users,
} from 'lucide-react';
import { Pulse } from '@/components/shared/Pulse';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DateRangeModal } from '@/components/modals/DateRangeModal';
import { useSearchStore, type SearchTab } from '@/stores/searchStore';
import { unsplash, photo } from '@/lib/utils/img';
import { brand } from '@/lib/theme/tokens';
import { letterReveal, staggerContainer } from '@/lib/motion/variants';
import { cn } from '@/lib/utils/cn';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';

const TABS: { key: SearchTab; icon: typeof Mountain }[] = [
  { key: 'Trek', icon: Mountain },
  { key: 'Stay', icon: Compass },
  { key: 'Boat', icon: Sailboat },
  { key: 'Ski', icon: Snowflake },
  { key: 'Ride', icon: Bike },
  { key: 'Guide', icon: User },
];

const WHERES = [
  'Pahalgam, Kashmir',
  'Gulmarg',
  'Srinagar · Dal Lake',
  'Sonamarg',
  'Doodhpathri',
  'Gurez Valley',
  'Aru Valley',
];

const STATUS_CHIPS = [
  { color: brand.ok, label: 'Spring trek season · 142 routes open' },
  { color: brand.ice, label: 'Apharwat · last lifts May 18' },
  { color: brand.saffron, label: 'Tulip Garden · in bloom' },
] as const;

const TRUST_LINKS = [
  { icon: Ship, label: 'Verified · police-checked guides' },
  { icon: Clock, label: '24/7 trip support · WhatsApp' },
  { icon: Award, label: 'Locally headquartered · Srinagar' },
  { icon: Leaf, label: '8% of profits → cleanup' },
] as const;

export function Hero() {
  const navigate = useNavigate();
  const { tab, where, startDay, endDay, guests, setTab, setWhere, setGuests } = useSearchStore();

  const [dateOpen, setDateOpen] = useState(false);
  const [pickedRange, setPickedRange] = useState<DateRange | undefined>(undefined);
  const [whereOpen, setWhereOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [whereQuery, setWhereQuery] = useState('');

  const filteredWheres = WHERES.filter((w) =>
    w.toLowerCase().includes(whereQuery.toLowerCase()),
  );

  const datesLabel = pickedRange?.from
    ? pickedRange.to
      ? `${format(pickedRange.from, 'MMM d')} – ${format(pickedRange.to, 'MMM d')}`
      : format(pickedRange.from, 'MMM d')
    : `May ${startDay}${endDay ? ` – ${endDay}` : ''}`;

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    const params = new URLSearchParams({
      type: tab.toLowerCase(),
      where,
      guests: String(guests),
    });
    navigate(`/search?${params.toString()}`);
  }

  return (
    <section className="relative px-3 md:px-6">
      {/* Image canvas — height is content-driven on mobile, aspect-ratio on md+ */}
      <div className="relative overflow-hidden rounded-[20px] md:rounded-[28px] md:aspect-[16/9] xl:aspect-[16/7.5]">
        {/* Background image + overlays */}
        <div className="absolute inset-0">
          <img
            src={unsplash(photo.aerialPeaks, { w: 2400 })}
            alt="Aerial view of the Kashmir Himalaya at first light"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full scale-105 object-cover brightness-[0.55] saturate-[1.2]"
          />
          <div
            className="absolute inset-0 mix-blend-screen"
            style={{
              background: `
                radial-gradient(ellipse 60% 50% at 15% 20%, ${brand.teal}30, transparent 60%),
                radial-gradient(ellipse 50% 60% at 85% 70%, ${brand.chinar}26, transparent 60%),
                radial-gradient(ellipse 70% 40% at 50% 100%, ${brand.saffron}1f, transparent)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 30% 40%, transparent, rgba(0,0,0,0.55) 80%)',
            }}
          />
        </div>

        {/* Content — flows naturally on mobile, centered absolutely on md+ */}
        <div className="relative md:absolute md:inset-0 md:flex md:items-center">
          <div className="mx-auto w-full max-w-7xl px-5 pb-10 pt-16 md:px-10 md:pb-0 md:pt-0">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="max-w-4xl"
            >
              {/* status chips */}
              <motion.div variants={letterReveal} className="mb-6 flex flex-wrap gap-2">
                {STATUS_CHIPS.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-medium text-white backdrop-blur-md"
                    style={{ background: 'rgba(255,255,255,0.10)' }}
                  >
                    <Pulse color={c.color} />
                    {c.label}
                  </span>
                ))}
              </motion.div>

              {/* Headline */}
              <div className="overflow-hidden">
                <motion.h1
                  variants={letterReveal}
                  className="font-display text-5xl font-semibold leading-[0.95] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[110px]"
                  style={{ textShadow: '0 4px 60px rgba(0,0,0,0.5)' }}
                >
                  Find what&apos;s
                </motion.h1>
              </div>
              <div className="overflow-hidden">
                <motion.h1
                  variants={letterReveal}
                  className="font-display text-5xl font-medium italic leading-[0.95] tracking-tight sm:text-6xl md:text-7xl lg:text-[110px]"
                  style={{
                    background: `linear-gradient(135deg, #8BD8C2, ${brand.teal} 40%, ${brand.saffron})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  still uncharted.
                </motion.h1>
              </div>

              <motion.p
                variants={letterReveal}
                className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}
              >
                One marketplace for every trek, ski run, houseboat, Royal Enfield, and licensed
                local guide — in the only valley that still humbles people who&apos;ve seen
                everything.
              </motion.p>

              {/* Search bar */}
              <motion.form
                variants={letterReveal}
                onSubmit={handleSearch}
                className="relative mt-8 max-w-4xl rounded-[24px] border border-white/20 p-1.5 shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl"
                style={{ background: 'rgba(255,255,255,0.12)' }}
              >
                {/* tabs */}
                <div className="flex gap-1 overflow-x-auto px-1.5 pt-1.5">
                  {TABS.map((t) => {
                    const TabIcon = t.icon;
                    const active = tab === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={cn(
                          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] transition-all',
                          active
                            ? 'font-semibold text-white shadow-[0_8px_20px_var(--color-teal-glow)]'
                            : 'font-medium text-white/80 hover:text-white',
                        )}
                        style={
                          active
                            ? {
                                background:
                                  'linear-gradient(135deg, var(--color-teal), var(--color-teal-dark))',
                              }
                            : undefined
                        }
                      >
                        <TabIcon size={13} />
                        {t.key}
                      </button>
                    );
                  })}
                </div>

                {/* fields */}
                <div className="mt-1 grid grid-cols-1 gap-px md:grid-cols-[1.4fr_1fr_1fr_auto]">
                  {/* Where */}
                  <Popover open={whereOpen} onOpenChange={setWhereOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-colors hover:bg-white/5 md:border-r md:border-white/15"
                      >
                        <MapPin size={18} className="shrink-0 text-white/70" />
                        <span>
                          <span className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/55">
                            Where
                          </span>
                          <span className="mt-0.5 block text-sm font-medium text-white">
                            {where}
                          </span>
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-2" align="start">
                      {/* Search */}
                      <div className="mb-1 flex items-center gap-2 rounded-lg border border-[var(--glass-edge)] bg-[var(--glass)] px-3 py-2">
                        <Search size={13} className="shrink-0 text-ink-mute" />
                        <input
                          autoFocus
                          value={whereQuery}
                          onChange={(e) => setWhereQuery(e.target.value)}
                          placeholder="Search destinations…"
                          className="w-full bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-mute"
                        />
                      </div>

                      <div className="mt-1 max-h-[260px] overflow-y-auto">
                        {filteredWheres.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-ink-mute">
                            No matches for &ldquo;{whereQuery}&rdquo;
                          </div>
                        ) : (
                          filteredWheres.map((w) => (
                            <button
                              key={w}
                              type="button"
                              onClick={() => {
                                setWhere(w);
                                setWhereOpen(false);
                                setWhereQuery('');
                              }}
                              className={cn(
                                'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] transition-colors hover:bg-[var(--glass-hi)]',
                                where === w ? 'font-semibold text-ink' : 'text-ink',
                              )}
                            >
                              <MapPin
                                size={13}
                                className="shrink-0"
                                color={where === w ? brand.teal : 'currentColor'}
                                style={{ opacity: where === w ? 1 : 0.5 }}
                              />
                              {w}
                            </button>
                          ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* When */}
                  <button
                    type="button"
                    onClick={() => setDateOpen(true)}
                    className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-colors hover:bg-white/5 md:border-r md:border-white/15"
                  >
                    <CalendarIcon size={18} className="shrink-0 text-white/70" />
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/55">
                        When
                      </span>
                      <span className="mt-0.5 block text-sm font-medium text-white">
                        {datesLabel}
                      </span>
                    </span>
                  </button>

                  {/* Guests */}
                  <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-colors hover:bg-white/5"
                      >
                        <Users size={18} className="shrink-0 text-white/70" />
                        <span>
                          <span className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/55">
                            Guests
                          </span>
                          <span className="mt-0.5 block text-sm font-medium text-white">
                            {guests} adult{guests > 1 ? 's' : ''}
                          </span>
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-4" align="end">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-ink">Adults</div>
                          <div className="text-[11px] text-ink-dim">13 or older</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            aria-label="Decrease guests"
                            onClick={() => setGuests(guests - 1)}
                            className="grid h-8 w-8 place-items-center rounded-full border border-[var(--glass-edge-hi)] text-base text-ink transition-colors hover:bg-[var(--glass-hi)]"
                          >
                            −
                          </button>
                          <span className="min-w-[20px] text-center font-bold text-ink">
                            {guests}
                          </span>
                          <button
                            type="button"
                            aria-label="Increase guests"
                            onClick={() => setGuests(guests + 1)}
                            className="grid h-8 w-8 place-items-center rounded-full border border-[var(--glass-edge-hi)] text-base text-ink transition-colors hover:bg-[var(--glass-hi)]"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="md"
                        className="mt-3 w-full justify-center"
                        onClick={() => setGuestsOpen(false)}
                      >
                        Done
                      </Button>
                    </PopoverContent>
                  </Popover>

                  <Button type="submit" size="lg" className="m-1 h-auto rounded-2xl px-7 py-3.5">
                    <Search size={16} strokeWidth={2.2} />
                    Explore
                  </Button>
                </div>
              </motion.form>

              {/* Trust strip */}
              <motion.div
                variants={letterReveal}
                className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/80"
              >
                {TRUST_LINKS.map((l) => {
                  const Icon = l.icon;
                  return (
                    <span key={l.label} className="inline-flex items-center gap-1.5">
                      <Icon size={14} className="text-white/80" />
                      {l.label}
                    </span>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <DateRangeModal
        open={dateOpen}
        onOpenChange={setDateOpen}
        initial={pickedRange}
        onConfirm={(r) => setPickedRange(r)}
      />
    </section>
  );
}
